import { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import {
  CartScope,
  DRAFT_CART_SCOPE,
} from '../../redux/features/draftCart/cartScope';
import {
  cartActionsForScope,
  ScopedCartActions,
  selectWriteCartScope,
} from '../../redux/features/draftCart/cartScope.routing';

export interface ScopedCartWriteTarget {
  /** Which cart the surface reading this is currently editing. */
  scope: CartScope;
  /** `cartActions` or `draftCartActions` — the shared reducers, same payloads. */
  actions: ScopedCartActions;
  /**
   * The draft is the agent's private workspace: it never sends an `order`, and
   * nothing done to it may reach the customer. Surfaces read this to stay silent
   * — no `agent_interception`, no METRIC, no TTS — while they are editing it.
   *
   * This matters in `Intercept` as much as in `Shadow`. The middleware's TTS
   * sink (`app/middleware.ts:909`) only swallows speech while the agent is
   * silent, so in `Intercept` an unguarded surface would happily speak because
   * the agent touched a My Cart line.
   */
  isDraft: boolean;
}

/**
 * HITL-1617 — the component-facing half of the write-path routing.
 *
 * Gives a surface the cart it should be writing into, without it having to know
 * that a second cart exists. With `hitl_shadow_mode` off this always resolves to
 * `cartActions`, so the legacy path is the identical one it was.
 */
const useScopedCartActions = (): ScopedCartWriteTarget => {
  const scope = useAppSelector(selectWriteCartScope);

  return useMemo(
    () => ({
      scope,
      actions: cartActionsForScope(scope),
      isDraft: scope === DRAFT_CART_SCOPE,
    }),
    [scope]
  );
};

export default useScopedCartActions;
